import { useCallback, useMemo, useRef } from 'react';
import { FilterOptionsState } from '@mui/material';

export type AutocompleteFilterConfig<T> = {
  /** Limit the results returned */
  limit: number;
  /** Function to convert an option to a formatted string */
  stringify: (option: T) => string;
  /** Whether to match the input to the start of the option label or anywhere */
  matchFrom?: 'start' | 'any';
};

// https://stackoverflow.com/questions/990904/remove-accents-diacritics-in-a-string-in-javascript
function stripDiacritics(string: string): string {
  return string.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function normalizeString(string: string): string {
  return stripDiacritics(string.trim()).toLowerCase();
}

/**
 * Create a memoized filter function for an autocomplete list that limits
 * the number of options shown, returning both the function and the counts
 * shown when it is run. The returned filter normalizes case and accented
 * characters for matching.
 */
export function useAutocompleteFilter<T>(
  options: ReadonlyArray<T>,
  config: AutocompleteFilterConfig<T>
): (options: ReadonlyArray<T>, state: FilterOptionsState<T>) => Array<T> {
  const { limit, stringify, matchFrom = 'any' } = config;

  // Cache the normalized strings when options change - this is the key optimization
  // to avoid calling stringify and normalize on every filter pass
  const normalizedStrings = useMemo(() => {
    return options.map((option) => normalizeString(stringify(option)));
  }, [options, stringify]);

  // Use ref to store filter state that persists between renders for incremental filtering
  const cacheRef = useRef<{
    lastInputValue: string;
    lastFilteredIndices: Array<number>;
    lastNormalizedStrings: Array<string>;
  }>({
    lastInputValue: '',
    lastFilteredIndices: [],
    lastNormalizedStrings: [],
  });

  // Reset cache when options change
  const cache = cacheRef.current;
  if (cache.lastNormalizedStrings !== normalizedStrings) {
    cache.lastNormalizedStrings = normalizedStrings;
    cache.lastInputValue = '';
    cache.lastFilteredIndices = [];
  }

  const filterOptions = useCallback(
    (opts: ReadonlyArray<T>, state: FilterOptionsState<T>) => {
      const cache = cacheRef.current;
      const inputValue = normalizeString(state.inputValue);

      // Early exit for empty input
      if (!inputValue) {
        const result = opts.slice(0, limit);
        cache.lastInputValue = '';
        cache.lastFilteredIndices = [];
        return result;
      }

      // Optimization: incremental filtering
      const searchIndices: Iterable<number> =
        cache.lastInputValue && inputValue.startsWith(cache.lastInputValue)
          ? // If new input starts with previous input, we can filter only the previous results
            cache.lastFilteredIndices
          : // Full search through all options (keys() is an iterator, so we don't need to fill the array)
            new Array(options.length).keys();

      const matchedIndices: Array<number> = [];

      // Perform the filtering
      for (const index of searchIndices) {
        const normalizedString = normalizedStrings[index];

        const matches =
          matchFrom === 'start'
            ? normalizedString.startsWith(inputValue)
            : normalizedString.includes(inputValue);

        if (matches) {
          matchedIndices.push(index);
        }
      }

      const result = matchedIndices
        .slice(0, limit)
        .map((index) => options[index]);

      // Update cache and counts
      cache.lastInputValue = inputValue;
      cache.lastFilteredIndices = matchedIndices;

      return result;
    },
    [options, normalizedStrings, limit, matchFrom]
  );

  return filterOptions;
}
